import { useState } from "react";

export const normTel = (t) => {
  const d = (t || "").replace(/\D/g, "");
  if (!d) return null;
  if (d.length === 8) return `+569${d}`;
  if (d.length === 9 && d.startsWith("9")) return `+56${d}`;
  if (d.length === 12 && d.startsWith("5699")) return `+56${d.slice(3)}`;
  return `+${d}`;
};
import { HelpCircle } from "lucide-react";
import { PAISES, MONTOS, PROFESIONES, PEP_OPCIONES, PEP_AYUDA, REGIONES } from "../../lib/kycData";
import { TextIn, SelectIn, Group, F } from "./FormBits";
import { BigButton, StepTitle } from "./KycShell";

const PROPOSITOS = [["ayuda_familiar", "Ayuda a mi familia"], ["ahorro", "Ahorro"], ["pago_servicios_deudas", "Pago de servicios o deudas"], ["otro", "Otro"]];
export const ORIGEN_FONDOS = [["sueldo", "Sueldo"], ["independiente", "Trabajo independiente"], ["ahorros", "Ahorros"], ["ayuda_familiar", "Ayuda familiar"], ["otro", "Otro"]];

const PAIS_OPTS = PAISES.map((p) => [p.code, p.name]);
const REG_OPTS = REGIONES.map((r) => [r.n, r.n]);
const PROF_OPTS = [...PROFESIONES.map((p) => [p, p]), ["otro", "Otro"]];
const EMPTY_DOM = { calle: "", numero: "", depto: "", comuna: "", region: "" };

const Domicilio = ({ prefix, v, set }) => {
  const comunas = REGIONES.find((r) => r.n === v.region)?.c || [];
  return (
    <>
      <TextIn id={`${prefix}-calle`} label="Calle" required value={v.calle} onChange={(e) => set({ ...v, calle: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">
        <TextIn id={`${prefix}-numero`} label="Número" required value={v.numero} onChange={(e) => set({ ...v, numero: e.target.value })} />
        <TextIn id={`${prefix}-depto`} label="Depto. (opcional)" value={v.depto} onChange={(e) => set({ ...v, depto: e.target.value })} />
      </div>
      <SelectIn id={`${prefix}-region`} label="Región" required options={REG_OPTS} value={v.region} onChange={(e) => set({ ...v, region: e.target.value, comuna: "" })} />
      <SelectIn id={`${prefix}-comuna`} label="Comuna" required options={comunas.map((c) => [c, c])} placeholder={v.region ? "Elige tu comuna" : "Primero elige la región"} value={v.comuna} onChange={(e) => set({ ...v, comuna: e.target.value })} />
    </>
  );
};

const PepFields = ({ pep, set, extranjero }) => {
  const [help, setHelp] = useState(false);
  return (
    <Group title={extranjero ? "¿Eres o fuiste una persona expuesta políticamente (PEP) en Chile o en tu país de origen?" : "¿Eres una persona expuesta políticamente (PEP)?"}>
      {extranjero && <p className="text-sm text-slate-600" data-testid="pep-extranjero-ejemplos">Por ejemplo: ministro, parlamentario, alcalde, juez, alto mando militar o policial, o directivo de una empresa estatal, en Chile o en tu país de origen. También si eres familiar o cercano de alguien con esos cargos.</p>}
      <button type="button" onClick={() => setHelp(!help)} className="flex items-center gap-2 text-base underline text-slate-700" data-testid="pep-help-toggle">
        <HelpCircle size={18} /> ¿Qué cargos cuentan?
      </button>
      {help && <ul className="list-disc pl-5 text-sm text-slate-600 space-y-1" data-testid="pep-help">{PEP_AYUDA.map((x) => <li key={x}>{x}</li>)}</ul>}
      <div className="space-y-2">
        {PEP_OPCIONES.map(([v, l]) => (
          <label key={v} className={`flex gap-3 items-center p-3 rounded-lg border-2 cursor-pointer ${pep.tipo === v ? "border-slate-900" : "border-slate-200"}`}>
            <input type="radio" name="pep" value={v} checked={pep.tipo === v} onChange={() => set({ ...pep, tipo: v })} className="h-5 w-5 accent-slate-900" data-testid={`pep-${v}`} />
            <span className="text-base">{l}</span>
          </label>
        ))}
      </div>
      {pep.tipo && pep.tipo !== "no" && (
        <>
          {pep.tipo === "si_familiar" && <TextIn id="pep-relacion" label="Tu relación con esa persona" placeholder="Ej: hijo, cónyuge, socio" required value={pep.relacion} onChange={(e) => set({ ...pep, relacion: e.target.value })} />}
          <TextIn id="pep-cargo" label="Cargo" required value={pep.cargo} onChange={(e) => set({ ...pep, cargo: e.target.value })} />
          <SelectIn id="pep-pais" label="País" required options={PAIS_OPTS} value={pep.pais} onChange={(e) => set({ ...pep, pais: e.target.value })} />
          <TextIn id="pep-institucion" label="Institución" required value={pep.institucion} onChange={(e) => set({ ...pep, institucion: e.target.value })} />
          <TextIn id="pep-fecha-termino" label="Fecha de término (si ya terminó)" type="date" value={pep.fecha_termino} onChange={(e) => set({ ...pep, fecha_termino: e.target.value })} />
        </>
      )}
    </Group>
  );
};

const INIT = {
  telefono: "+569", correo: "", ocupacion: "", ocupacion_otro: "", proposito: "", proposito_otro: "", origen_fondos: "", origen_fondos_otro: "", monto_rango: "", pais_destino: "",
};

export const StepDatos = ({ brand, onSubmit, busy, nacionalidad, paso, prefill, telVerificado }) => {
  const pre = prefill || {};
  const [f, setF] = useState(() => ({ ...INIT, ...Object.fromEntries(Object.entries(pre).filter(([k, v]) => k in INIT && typeof v === "string")),
    telefono: normTel(telVerificado || pre.telefono) || INIT.telefono }));
  const [dom, setDom] = useState(() => ({ ...EMPTY_DOM, ...(pre.domicilio || {}) }));
  const [tribDistinto, setTribDistinto] = useState(false);
  const [trib, setTrib] = useState(EMPTY_DOM);
  const [pep, setPep] = useState({ tipo: "", cargo: "", pais: "", institucion: "", fecha_termino: "", relacion: "" });
  const b = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }) });

  const submit = (e) => {
    e.preventDefault();
    const nul = (x) => (x ? x : null);
    onSubmit({
      ...f, telefono: normTel(f.telefono) || f.telefono, ocupacion_otro: nul(f.ocupacion_otro),
      proposito_otro: nul(f.proposito_otro), origen_fondos_otro: nul(f.origen_fondos_otro), domicilio: { ...dom, depto: nul(dom.depto) },
      domicilio_tributario: tribDistinto ? { ...trib, depto: nul(trib.depto) } : null,
      pep: { tipo: pep.tipo, cargo: nul(pep.cargo), pais: nul(pep.pais), institucion: nul(pep.institucion), fecha_termino: nul(pep.fecha_termino), relacion: nul(pep.relacion) },
    });
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="kyc-step-datos">
      <StepTitle paso={paso} title="Algunos datos más">Estos datos no vienen en tu documento.</StepTitle>
      <Group title="Contacto">
        {telVerificado ? (
          <div className="space-y-1" data-testid="kyc-telefono-verificado">
            <p className="text-sm font-medium text-slate-700">Teléfono</p>
            <p className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-base">
              {normTel(telVerificado)}
              <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-emerald-100 text-emerald-800" data-testid="kyc-telefono-sello">Verificado</span>
            </p>
          </div>
        ) : (
          <TextIn id="telefono" label="Teléfono" type="tel" required autoComplete="tel" value={f.telefono}
            onChange={(e) => setF({ ...f, telefono: e.target.value })} onBlur={() => setF({ ...f, telefono: normTel(f.telefono) || f.telefono })} />
        )}
        <TextIn id="correo" label="Correo electrónico" type="email" required autoComplete="email" {...b("correo")} />
      </Group>
      <Group title="Tu domicilio en Chile">
        <Domicilio prefix="dom" v={dom} set={setDom} />
        <label className="flex gap-3 items-center text-base">
          <input type="checkbox" checked={tribDistinto} onChange={(e) => setTribDistinto(e.target.checked)} className="h-5 w-5 accent-slate-900" data-testid="kyc-trib-distinto" />
          Mi domicilio tributario es distinto
        </label>
        {tribDistinto && <Domicilio prefix="trib" v={trib} set={setTrib} />}
      </Group>
      <Group title="Tu trabajo y tus envíos">
        <SelectIn id="ocupacion" label="Profesión u oficio" required options={PROF_OPTS} {...b("ocupacion")} />
        {f.ocupacion === "otro" && <TextIn id="ocupacion-otro" label="¿Cuál?" required {...b("ocupacion_otro")} />}
        <SelectIn id="proposito" label="¿Para qué envías dinero?" required options={PROPOSITOS} {...b("proposito")} />
        {f.proposito === "otro" && <TextIn id="proposito-otro" label="Cuéntanos para qué" required {...b("proposito_otro")} />}
        <SelectIn id="origen-fondos" label="¿De dónde viene el dinero que envías?" required options={ORIGEN_FONDOS} {...b("origen_fondos")} />
        {f.origen_fondos === "otro" && <TextIn id="origen-fondos-otro" label="Cuéntanos de dónde" required {...b("origen_fondos_otro")} />}
        <SelectIn id="monto-rango" label="¿Cuánto envías al mes, más o menos?" required options={MONTOS} {...b("monto_rango")} />
        <SelectIn id="pais-destino" label="¿A qué país envías dinero principalmente?" required options={PAIS_OPTS} {...b("pais_destino")} />
      </Group>
      <PepFields pep={pep} set={setPep} extranjero={!!nacionalidad && nacionalidad !== "CL"} />
      <F id="x" label="">
        <BigButton type="submit" color={brand.color} disabled={busy || !pep.tipo} data-testid="datos-continue-button">Continuar</BigButton>
      </F>
    </form>
  );
};
